/**
 * OPERATOR RESET: 3-Week Reconditioning Program
 *
 * Audience: Advanced operators (Chassis Cut / Operator Strength alumni) returning
 * from 60+ days of detraining due to injury, deployment, or life event. They retain
 * movement competence but their connective tissue and aerobic base are deeply
 * degraded. VO2max detrains roughly 2x faster than 1RM strength
 * (Mujika & Padilla, 2000a/b; Pedlar et al.), which is why aerobic emphasis is
 * mandatory in every week here — not optional.
 *
 * Design basis:
 *   - Bompa anatomical-adaptation phase (Week 1): full-body, tempo-driven sessions
 *     to restore tendon/ligament tolerance before reintroducing competitive loads.
 *   - Mujika & Padilla + Pedlar aerobic detraining literature: Z2 emphasis in
 *     every week to rebuild mitochondrial density and capillarization.
 *   - NSCA TSAC return-to-training guidance: progressive RPE caps, readiness
 *     check before re-entry into operator-level programming.
 *
 * STRUCTURE
 *   Week 1 — Reconditioning  (4 sessions: D1/D3/D5/D7) — RPE 5-6, x0.5 load/volume
 *   Week 2 — Bridging        (5 sessions: D1/D2/D3/D5/D7) — RPE 6-7, x0.7 load/volume
 *   Week 3 — Loading         (4 sessions: D1/D3/D5/D7) — RPE 7-8, x0.85 load/volume
 *
 * Final session (W3D7) is a readiness check. If clean across pull-up max,
 * squat at 80% prior load, and sustained 30-min Z2, the operator graduates to
 * Chassis Cut / Operator Strength. Otherwise, repeat W3.
 *
 * ALL exercises sourced from internal exerciseDatabase / freeDB.
 */

import type {
  WorkoutProgram,
  WorkoutTemplateDay,
  ProgramMetadata,
  ProgramPeriodizationModel,
} from '../types/program';

// ============================================================================
// METADATA
// ============================================================================

const metadata: ProgramMetadata = {
  id: 'operator-reset-3week',
  name: 'OPERATOR RESET: 3-Week Reconditioning',
  description:
    'Three-week reconditioning protocol for advanced operators returning from 60+ days of detraining (injury, deployment, life event). Week 1 rebuilds movement, connective tissue, and aerobic base. Week 2 reintroduces strength signals, sled, and carries. Week 3 loads at operator intensity and ends with a readiness check before graduating to Chassis Cut.',
  level: 'operator',
  experienceLevel: 'advanced',
  duration: 3,
  daysPerWeek: 5,
  goals: ['strength', 'conditioning', 'work-capacity', 'tactical-performance'],
  difficulty: 'advanced',
  tags: ['ramp-up', 're-entry', 'reconditioning', 'extended-break', 'advanced'],
  emphasis: 'Reconditioning → strength signal → operator-level loading',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  usesPeriodization: true,
  hasRangeDay: false,
  requiredEquipment: 'full-gym',
  // Reset cycle leans on aerobic base for HRV restoration. ~90 min/wk
  // Z2 across 4-5 sessions (most days have a 20-25 min walk component).
  weeklyZ2Target: 90,
};

// ============================================================================
// PERIODIZATION — Linear, 3 phases
// ============================================================================

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    {
      weeks: [1],
      type: 'accumulation',
      name: 'Reconditioning — Pattern, Tendon, Aerobic Base',
      focus:
        'Full-body tempo work. Restore connective tissue tolerance and rebuild Z2 floor. RPE 5-6 (RIR 4+). No grinding reps. Aerobic emphasis is non-negotiable — 60+ days off destroys VO2max faster than strength.',
      intensityMultiplier: 0.5,
      volumeMultiplier: 0.5,
      trainingParameters: {
        repRange: [10, 15],
        setsMultiplier: 0.5,
        restPeriod: '90-120s',
      },
    },
    {
      weeks: [2],
      type: 'accumulation',
      name: 'Bridging — Strength Signal + Carries + Sled',
      focus:
        'Reintroduce strength signals at RPE 6-7. Sled, carries, and HIIT re-entry. Two upper / two lower / one mixed conditioning. Build tolerance for split-style loading.',
      intensityMultiplier: 0.7,
      volumeMultiplier: 0.7,
      progression: {
        type: 'linear',
        startIntensity: 0.6,
        endIntensity: 0.75,
        weeklyIncrease: 0.10,
      },
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 0.7,
        restPeriod: '90-120s',
      },
    },
    {
      weeks: [3],
      type: 'intensification',
      name: 'Loading — Operator-Level + Readiness Check',
      focus:
        'Operator-level loading at RPE 7-8 (RIR 2-3). Back squat, bench, deadlift, pull-up signals. Final session is a readiness check — pull-up max, squat at 80% prior, 30-min sustained Z2. Green light = graduate to Chassis Cut.',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.85,
      progression: {
        type: 'linear',
        startIntensity: 0.78,
        endIntensity: 0.85,
        weeklyIncrease: 0.07,
      },
      trainingParameters: {
        repRange: [5, 10],
        setsMultiplier: 0.85,
        restPeriod: '2-3 min',
      },
    },
  ],
};

// ============================================================================
// SHARED: REST DAY
// ============================================================================

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

// ============================================================================
// WEEK 1: RECONDITIONING — Full Body (4 days: D1, D3, D5, D7)
// Focus: movement quality, connective tissue prep, aerobic base
// RPE 5-6. Volume x0.5. Intensity x0.5.
// ============================================================================

// --- W1 DAY 1: FULL BODY A — Squat + Push/Pull + Z2 ---
const w1Day1: WorkoutTemplateDay = {
  name: 'Full Body A — Squat + Push/Pull + Z2',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep + Mobility',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '6 min', format: 'prep', details: 'Bike or incline walk — extended warm-up after 60+ days off. Joints need time to wake up.' },
        { name: 'Cat-Cow', sets: 2, reps: 10, format: 'prep' },
        { name: 'Hip Flexor Opener', duration: '45s/side', format: 'prep' },
        { name: 'Ankle Rocks', sets: 2, reps: '10/side', format: 'prep' },
        { name: 'TKE', sets: 2, reps: '15/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep' },
      ],
    },
    {
      name: 'Squat Pattern — Tempo',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 10, intensity: 'RPE 5-6', rest: '120s', details: '4-sec eccentric, 1-sec pause at bottom. Pattern over weight — this is connective tissue prep, not strength.' },
      ],
    },
    {
      name: 'Push/Pull — Controlled',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Push-Ups', sets: 3, reps: '8-12', intensity: 'bodyweight', rest: '0:00', details: '3-sec eccentric. Incline scale if needed — no ego after 60 days off.' },
        { name: 'Dumbbell Row', sets: 3, reps: '10/side', intensity: 'RPE 5-6', rest: '90s', details: '1-sec pause at top. Rebuild scapular control.' },
      ],
    },
    {
      name: 'Accessory — Posterior + Core',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Romanian Deadlift', sets: 2, reps: 12, intensity: 'light', rest: '0:00', details: 'Dumbbells. 3-sec eccentric. Wake up hamstrings without taxing them.' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '60s', details: 'Reactivate deep core bracing before any heavy loading returns.' },
      ],
    },
    {
      name: 'Zone 2 — Aerobic Base Rebuild',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '25 min', details: 'Z2 HR (~60-70% max). Conversational pace. After 60+ days off, VO2max is your weakest link — rebuild it daily.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '3 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W1 DAY 3: FULL BODY B — Hinge + Overhead + Carry + Z2 ---
const w1Day3: WorkoutTemplateDay = {
  name: 'Full Body B — Hinge + Overhead + Carry + Z2',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '6 min', format: 'prep', details: 'Rower — easy pace, find rhythm' },
        { name: 'Hip Hinge Drill', sets: 2, reps: 10, format: 'prep' },
        { name: 'Frog Stretch', sets: 2, reps: '8/side', format: 'prep' },
        { name: 'Glute Bridge Iso', sets: 2, reps: '20s + 8 reps', format: 'prep' },
        { name: 'Shoulder Y-T-W', sets: 1, reps: '6 each', format: 'prep' },
        { name: 'Face Pull', sets: 2, reps: 15, format: 'prep' },
      ],
    },
    {
      name: 'Hinge Pattern — Tempo',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 3, reps: 10, intensity: 'RPE 5-6', rest: '120s', details: '4-sec eccentric. Dumbbells preferred. Re-pattern hip hinge — hamstrings have not loaded properly in 60+ days.' },
      ],
    },
    {
      name: 'Overhead + Row',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Shoulder Press', sets: 3, reps: 10, intensity: 'RPE 5-6', rest: '0:00', details: 'Seated. Controlled, full ROM. Shoulders re-learning the overhead position.' },
        { name: 'Seated Cable Row', sets: 3, reps: 12, intensity: 'light', rest: '90s', details: '2-sec squeeze. Posture emphasis.' },
      ],
    },
    {
      name: 'Carry — Re-Introduction',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 3, reps: '30m', intensity: 'light-moderate', rest: '90s', details: 'Conservative load. Grip endurance has detrained — start light, build posture and tempo.' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 2, reps: '10/side', rest: '60s', details: 'Anti-rotation bracing.' },
        { name: 'Side Plank', sets: 2, reps: '20s/side', rest: '30s' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '25 min', details: 'Z2. Steady. Mandatory.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Pigeon Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '3 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W1 DAY 5: FULL BODY C — Pull Volume + Single Leg + Z2 ---
const w1Day5: WorkoutTemplateDay = {
  name: 'Full Body C — Pull Volume + Single Leg + Z2',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Bike' },
        { name: 'Dead Hang', duration: '20-30s', format: 'prep', details: 'Re-acclimate grip and lat tissue to hanging load.' },
        { name: 'Straight-Arm Pulldown', sets: 2, reps: 12, format: 'prep' },
        { name: 'T-Spine Extension', sets: 2, reps: 8, format: 'prep' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '25s', format: 'prep' },
        { name: 'Band Lateral Walk', sets: 2, reps: '10 steps each way', format: 'prep' },
      ],
    },
    {
      name: 'Pull — Volume',
      type: 'strength',
      exercises: [
        { name: 'Lat Pulldown', sets: 3, reps: 12, intensity: 'RPE 5-6', rest: '90s', details: 'Full ROM, 3-sec eccentric. Rebuild lat tissue tolerance before pull-ups return in W2.' },
        { name: 'Chest Supported Row', sets: 3, reps: 10, intensity: 'RPE 5-6', rest: '90s', details: '1-sec pause. Scapular retraction.' },
      ],
    },
    {
      name: 'Single Leg — Stability',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Bulgarian Split Squat', sets: 2, reps: '8/side', intensity: 'bodyweight or light DBs', rest: '0:00', details: 'Controlled descent. Connective tissue tolerance is the goal — not loading.' },
        { name: 'Single-Leg Romanian Deadlift', sets: 2, reps: '8/side', intensity: 'light', rest: '90s', details: 'DB counterbalance. Proprioception rebuild.' },
      ],
    },
    {
      name: 'Upper Accessory',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Face Pull', sets: 2, reps: 15, rest: '0:00' },
        { name: 'Dumbbell Curl', sets: 2, reps: 12, intensity: 'light', rest: '60s', details: 'Bicep tendons need re-loading after extended time off. Easy sets matter.' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '25-30 min', details: 'Push duration slightly from D1/D3. Mandatory Z2.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery' },
        { name: 'Hip Flexor Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '3 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W1 DAY 7: FULL BODY D — Push Focus + Posterior + LONG Z2 ---
const w1Day7: WorkoutTemplateDay = {
  name: 'Full Body D — Push + Posterior + Long Z2',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '6 min', format: 'prep' },
        { name: 'Serratus Wall Slide', sets: 2, reps: 10, format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep' },
        { name: 'Soleus Raise', sets: 2, reps: 15, format: 'prep' },
        { name: 'Step-Downs', sets: 2, reps: '6/side', format: 'prep', details: '3-sec eccentric' },
      ],
    },
    {
      name: 'Push — Tempo',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 3, reps: 10, intensity: 'RPE 5-6', rest: '0:00', details: '3-sec eccentric. Re-pattern incline press without ego load.' },
        { name: 'Dumbbell Row', sets: 3, reps: '10/side', intensity: 'RPE 5-6', rest: '90s' },
      ],
    },
    {
      name: 'Posterior Chain',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Hip Thrust', sets: 3, reps: 12, intensity: 'moderate', rest: '0:00', details: 'Rebuild glute activation before W2 hinge signal.' },
        { name: 'Back Extension', sets: 2, reps: 12, intensity: 'bodyweight', rest: '90s' },
      ],
    },
    {
      name: 'Core + Stability',
      type: 'accessory',
      exercises: [
        { name: 'McGill Curl-Up', sets: 2, reps: 8, rest: '0:00', details: '6-sec holds' },
        { name: 'Pallof Press', sets: 2, reps: '10/side', rest: '60s' },
      ],
    },
    {
      name: 'Zone 2 — Extended',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '35 min', details: 'Longest Z2 of W1. Floor for the aerobic base. If conversational and steady — W2 is unlocked.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Glute Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: 'Soleus Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '3 min', format: 'recovery' },
      ],
    },
  ],
};

// ============================================================================
// WEEK 2: BRIDGING — Strength Signal + Carries + Sled (5 days: D1, D2, D3, D5, D7)
// RPE 6-7. Volume x0.7. Intensity x0.7.
// ============================================================================

// --- W2 DAY 1: LOWER — Squat Signal + Sled + Farmer ---
const w2Day1: WorkoutTemplateDay = {
  name: 'Lower — Squat Signal + Sled + Farmer',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '4 min', format: 'prep', details: 'Bike' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30s', format: 'prep' },
        { name: 'TKE', sets: 2, reps: '15/side', format: 'prep' },
        { name: 'Band Lateral Walk', sets: 2, reps: '10 steps each way', format: 'prep' },
        { name: 'Step-Downs', sets: 2, reps: '6/side', format: 'prep', details: '3-sec eccentric' },
      ],
    },
    {
      name: 'Strength Signal — Squat',
      type: 'strength',
      exercises: [
        { name: 'Leg Press', sets: 4, reps: '8-10', intensity: 'RPE 7', rest: '2 min', details: 'First real squat-pattern loading. Build across sets. Last set should feel like work — not a grind.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Leg Extension', sets: 3, reps: '12-15', intensity: 'progressive', rest: '0:00' },
        { name: 'Seated Ham Curl', sets: 3, reps: '10-12', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Sled Re-Introduction',
      type: 'conditioning',
      exercises: [
        { name: 'Sled Push', sets: 5, reps: '15m', intensity: 'light-moderate', rest: '60s', details: 'First sled work in 60+ days. Conservative load. Build confidence, not PRs.' },
        { name: 'Farmer Carry', sets: 3, reps: '40m', intensity: 'moderate', rest: '60s' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 3, reps: '10/side', rest: '60s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 2: UPPER — Pull Signal + Push + HIIT Re-Intro ---
const w2Day2: WorkoutTemplateDay = {
  name: 'Upper — Pull Signal + Push + HIIT',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '4 min', format: 'prep', details: 'Rower' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 12, format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep' },
        { name: 'Face Pull', sets: 2, reps: 15, format: 'prep' },
        { name: 'T-Spine Extension', sets: 1, reps: 8, format: 'prep' },
        { name: 'Dead Hang', duration: '20s', format: 'prep' },
      ],
    },
    {
      name: 'Strength Signal — Pull',
      type: 'strength',
      exercises: [
        { name: 'Weighted Pull-Up', sets: 4, reps: '4-6', intensity: 'RPE 7', rest: '2-3 min', details: 'First weighted pull in 60+ days. Bodyweight first set; add weight only if clean. Scale to Lat Pulldown if pull-up fails.' },
        { name: 'Lat Pulldown', sets: 0, reps: '8-10', intensity: 'RPE 7', rest: '90s', details: 'SCALE OPTION: use this if weighted pull-up form breaks. 3 sets.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 3, reps: '8-10', intensity: 'RPE 6-7', rest: '0:00' },
        { name: 'Chest Supported Row', sets: 3, reps: '8-10', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Cable Lateral Raise', sets: 3, reps: '12-15', rest: '0:00' },
        { name: 'Reverse Pec Deck', sets: 3, reps: '12-15', rest: '60s' },
      ],
    },
    {
      name: 'HIIT Re-Intro',
      type: 'conditioning',
      exercises: [
        { name: 'Row/Bike Sprint', sets: 5, reps: 1, format: 'intervals', rest: '60s', details: '15s hard / 60s easy x5. First true intensity since pre-break. Shorter than Chassis Cut HIIT on purpose.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 3: Z2 + HEAVY CARRIES + CHASSIS CORE ---
const w2Day3: WorkoutTemplateDay = {
  name: 'Z2 + Heavy Carries + Chassis Core',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep' },
        { name: 'TKE', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Band External Rotation', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '35-40 min', details: 'Building toward Chassis Cut Z2 (35-50 min). Conversational pace. This is the aerobic anchor of W2.' },
      ],
    },
    {
      name: 'Carry Density',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 5, reps: '40m', intensity: 'moderate-heavy', rest: '45s', details: 'Heavier than W1. Grip and trunk endurance test.' },
        { name: 'Suitcase Carry', sets: 3, reps: '30m/side', intensity: 'moderate', rest: '45s' },
      ],
    },
    {
      name: 'Chassis Core',
      type: 'accessory',
      format: 'circuit',
      exercises: [
        { name: 'Pallof Press', sets: 2, reps: '12/side', rest: '0:00' },
        { name: 'Side Plank', sets: 2, reps: '30s/side', rest: '0:00' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '60s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Soleus Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 5: LOWER — Hinge Signal + Backward Drag ---
const w2Day5: WorkoutTemplateDay = {
  name: 'Lower — Hinge Signal + Backward Drag',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '4 min', format: 'prep', details: 'Bike' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep' },
        { name: 'Seated Ham Curl', sets: 1, reps: 15, format: 'prep', details: 'Light warm-up' },
        { name: 'Glute Bridge Iso', sets: 1, reps: '20s + 8 reps', format: 'prep' },
        { name: 'Frog Stretch', sets: 1, reps: '8/side', format: 'prep' },
        { name: 'Split-Stance Hinge', sets: 1, reps: '8/side', format: 'prep' },
      ],
    },
    {
      name: 'Strength Signal — Hinge',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 4, reps: '6-8', intensity: 'RPE 7', rest: '2-3 min', details: 'Barbell. First real hinge load in 60+ days. If form breaks, drop weight immediately.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Seated Ham Curl', sets: 3, reps: '10-12', intensity: 'progressive', rest: '0:00' },
        { name: 'Tibialis Raise', sets: 3, reps: '15-20', rest: '90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Hip Thrust', sets: 3, reps: '10-12', intensity: 'progressive', rest: '0:00' },
        { name: 'Cable Crunch', sets: 3, reps: '10-15', rest: '90s' },
      ],
    },
    {
      name: 'Backward Drag',
      type: 'conditioning',
      exercises: [
        { name: 'Backward Drag', sets: 8, reps: '15m', intensity: 'moderate', rest: '40s', details: 'First backward drag in 60+ days. Light-moderate. Knees should feel better — not worse — after this.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Glute Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 7: UPPER — Press Signal + Pull Volume + Z2 ---
const w2Day7: WorkoutTemplateDay = {
  name: 'Upper — Press Signal + Pull Volume + Z2',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '4 min', format: 'prep', details: 'Rower' },
        { name: 'Dead Hang', duration: '20s', format: 'prep' },
        { name: 'Straight-Arm Pulldown', sets: 2, reps: 12, format: 'prep' },
        { name: 'Shoulder Y-T-W', sets: 1, reps: '6 each', format: 'prep' },
        { name: 'Serratus Wall Slide', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Strength Signal — Press',
      type: 'strength',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 4, reps: '6-8', intensity: 'RPE 7-8', rest: '2-3 min', details: 'Heaviest pressing of W2. Sets up W3 bench press signal. Solid here = ready for barbell next week.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Lat Pulldown', sets: 3, reps: '8-10', intensity: 'progressive', rest: '0:00' },
        { name: 'Landmine Press', sets: 3, reps: '8-10/side', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: '1-Arm Cable Row', sets: 3, reps: '10/side', intensity: 'progressive', rest: '0:00' },
        { name: 'Machine Chest Press', sets: 2, reps: '10-12', intensity: 'progressive', rest: '60s' },
      ],
    },
    {
      name: 'Arms',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Barbell Curl', sets: 2, reps: '8-12', rest: '0:00' },
        { name: 'Triceps Pressdown', sets: 2, reps: '10-15', rest: '60s' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '20 min', details: 'Mandatory Z2. Aerobic floor maintained even on heavy upper day.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// ============================================================================
// WEEK 3: LOADING — Operator-Level (4 days: D1, D3, D5, D7)
// RPE 7-8. Volume x0.85. Intensity x0.85.
// Final day = READINESS CHECK
// ============================================================================

// --- W3 DAY 1: LOWER — Back Squat Signal + Sled + Carries ---
const w3Day1: WorkoutTemplateDay = {
  name: 'Lower — Back Squat Signal + Sled + Carries',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '4 min', format: 'prep', details: 'Bike' },
        { name: 'Ankle Rocks', sets: 2, reps: '10/side', format: 'prep' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30s', format: 'prep' },
        { name: 'TKE', sets: 2, reps: '15/side', format: 'prep' },
        { name: 'Band Lateral Walk', sets: 2, reps: '10 steps each way', format: 'prep' },
        { name: 'Goblet Squat', sets: 2, reps: 8, format: 'prep', details: 'Warm-up only — pattern primer.' },
      ],
    },
    {
      name: 'Strength Signal — Back Squat',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 5, reps: '5-6', intensity: 'RPE 8', rest: '3 min', details: 'First barbell back squat in 60+ days. Target ~75-80% of pre-break working weight. Bail out at form failure — no exceptions.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Bulgarian Split Squat', sets: 3, reps: '8/side', intensity: 'progressive', rest: '0:00' },
        { name: 'Leg Extension', sets: 3, reps: '10-12', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Sled + Carries',
      type: 'conditioning',
      exercises: [
        { name: 'Sled Push', sets: 6, reps: '20m', intensity: 'moderate-heavy', rest: '60s', details: 'Operator-level sled load. Aggressive turnover. If grip and posture hold, you are ready for Chassis Cut sled work.' },
        { name: 'Farmer Carry', sets: 3, reps: '40m', intensity: 'heavy', rest: '60s' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 3, reps: '10/side', rest: '60s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W3 DAY 3: UPPER — Pull-Up + Bench Press Signal + HIIT ---
const w3Day3: WorkoutTemplateDay = {
  name: 'Upper — Pull-Up + Bench Press Signal + HIIT',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '4 min', format: 'prep', details: 'Rower' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep' },
        { name: 'Face Pull', sets: 2, reps: 15, format: 'prep' },
        { name: 'Dead Hang', duration: '25s', format: 'prep' },
        { name: 'Push-Ups', sets: 1, reps: 10, format: 'prep', details: 'Warm-up only — bench primer.' },
      ],
    },
    {
      name: 'Strength Signal — Bench Press',
      type: 'strength',
      exercises: [
        { name: 'Bench Press', sets: 5, reps: '5-6', intensity: 'RPE 8', rest: '3 min', details: 'First barbell bench in 60+ days. Target ~75-80% of pre-break working weight. Spotter required.' },
      ],
    },
    {
      name: 'Pull-Up Volume',
      type: 'strength',
      exercises: [
        { name: 'Pull-Up', sets: 4, reps: '5-8', intensity: 'RPE 7-8', rest: '2 min', details: 'Strict, full ROM. Weighted only if 8 clean bodyweight reps are accessible. Otherwise pure bodyweight.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Shoulder Press', sets: 3, reps: '8-10', intensity: 'progressive', rest: '0:00' },
        { name: 'Chest Supported Row', sets: 3, reps: '8-10', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'HIIT',
      type: 'conditioning',
      exercises: [
        { name: 'Row/Bike Sprint', sets: 6, reps: 1, format: 'intervals', rest: '40s', details: '20s hard / 40s easy x6. Chassis Cut-level intensity. If you can hold output across all 6, the engine is back.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W3 DAY 5: LOWER — Deadlift Signal + Farmer + Z2 ---
const w3Day5: WorkoutTemplateDay = {
  name: 'Lower — Deadlift Signal + Farmer + Z2',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Bike' },
        { name: 'Hip Hinge Drill', sets: 2, reps: 10, format: 'prep' },
        { name: 'Glute Bridge Iso', sets: 2, reps: '20s + 8 reps', format: 'prep' },
        { name: 'Frog Stretch', sets: 2, reps: '8/side', format: 'prep' },
        { name: 'Romanian Deadlift', sets: 2, reps: 8, format: 'prep', details: 'Light DB warm-up — hinge primer before the barbell.' },
      ],
    },
    {
      name: 'Strength Signal — Deadlift',
      type: 'strength',
      exercises: [
        { name: 'Deadlift', sets: 4, reps: '4-6', intensity: 'RPE 7-8', rest: '3 min', details: 'First barbell deadlift in 60+ days. Target ~70-78% of pre-break working weight. Crisp setup, neutral spine, full lockout — bail if form drifts.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Seated Ham Curl', sets: 3, reps: '10-12', intensity: 'progressive', rest: '0:00' },
        { name: 'Hip Thrust', sets: 3, reps: '8-10', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 4, reps: '40m', intensity: 'heavy', rest: '60s', details: 'Heaviest farmer carry of the program. Grip endurance is back online.' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '25 min', details: 'Z2. Mandatory. Aerobic floor is non-negotiable even with full barbell loading back.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Glute Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W3 DAY 7: READINESS CHECK — Test + Debrief ---
const w3Day7: WorkoutTemplateDay = {
  name: 'Readiness Check — Green Light or Repeat W3',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep' },
        { name: 'Hip Airplanes', sets: 1, reps: '5/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
        { name: 'TKE', sets: 2, reps: '15/side', format: 'prep' },
        { name: 'Band External Rotation', sets: 1, reps: '12/side', format: 'prep' },
        { name: 'Dead Hang', duration: '25s', format: 'prep' },
      ],
    },
    {
      name: 'Readiness Test 1 — Pull-Up Max',
      type: 'assessment',
      exercises: [
        { name: 'Pull-Up', sets: 1, reps: 'Max', intensity: 'bodyweight', rest: '3 min', details: 'Max strict bodyweight pull-ups. Compare to pre-break baseline. >=80% of prior max = green light component.' },
      ],
    },
    {
      name: 'Readiness Test 2 — Back Squat @ 80% Prior',
      type: 'assessment',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: 5, intensity: 'RPE 8', rest: '3 min', details: 'One working set at 80% of your pre-break working weight (NOT 1RM — your typical heavy-set load). Clean reps, full depth, no form drift = green light component.' },
      ],
    },
    {
      name: 'Readiness Test 3 — Sustained Z2',
      type: 'assessment',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '30 min', details: 'Sustained Z2 (HR stays in band for full duration, no drift above zone). If HR drifts up or RPE climbs past 6 = aerobic base not yet rebuilt = repeat W3.' },
      ],
    },
    {
      name: 'Armor Core',
      type: 'accessory',
      format: 'circuit',
      exercises: [
        { name: 'McGill Curl-Up', sets: 2, reps: 8, rest: '0:00', details: '8-sec holds' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '0:00' },
        { name: 'Pallof Press', sets: 2, reps: '10/side', rest: '60s' },
      ],
    },
    {
      name: 'Debrief',
      type: 'recovery',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '5 min', details: 'Cool-down. Debrief: ALL THREE tests clean? Joints feel good? Sleep and energy stable across the week? If YES on all four — start Chassis Cut tomorrow. If NO on any one — repeat W3 before re-entry.' },
        { name: 'Hip Flexor Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '3 min', format: 'recovery' },
      ],
    },
  ],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const operatorReset3Week: WorkoutProgram = {
  metadata,
  periodization,
  workouts: [],
  templates: {
    // Week 1: Reconditioning — 4 training days (D1, D3, D5, D7), rest D2, D4, D6
    'week1-day1': w1Day1,
    'week1-day2': restDay,
    'week1-day3': w1Day3,
    'week1-day4': restDay,
    'week1-day5': w1Day5,
    'week1-day6': restDay,
    'week1-day7': w1Day7,

    // Week 2: Bridging — 5 training days (D1, D2, D3, D5, D7), rest D4, D6
    'week2-day1': w2Day1,
    'week2-day2': w2Day2,
    'week2-day3': w2Day3,
    'week2-day4': restDay,
    'week2-day5': w2Day5,
    'week2-day6': restDay,
    'week2-day7': w2Day7,

    // Week 3: Loading — 4 training days (D1, D3, D5, D7), rest D2, D4, D6
    // D7 is the readiness check
    'week3-day1': w3Day1,
    'week3-day2': restDay,
    'week3-day3': w3Day3,
    'week3-day4': restDay,
    'week3-day5': w3Day5,
    'week3-day6': restDay,
    'week3-day7': w3Day7,
  },
};
